import { Link } from 'react-router';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';

export interface StatTileProps {
  label: string;
  value: string | number;
  /** When true, the tile is a link — uses `<Link to={href}>`. */
  clickable?: boolean;
  /** Required when `clickable=true`. React Router relative path under `/app/`. */
  href?: string;
  /** Optional subtitle (cold-start CTA hint, etc.). */
  subtitle?: string;
  className?: string;
}

/**
 * `<StatTile>` — single tile inside `<StatsStrip>`.
 *
 * Anatomy (UI-SPEC § 3.6):
 *   `<GlassPanel variant="strong" h-24 p-5 grid grid-rows-[auto_auto] gap-2 items-start>`
 *   row 1: label  (`text-sm font-semibold text-foam-50/70`)
 *   row 2: value  (`text-xl font-semibold text-foam-50 tabular-nums`)
 *
 * Clickable variant (Top Fit + Freshest only — § 1.1.1 #6):
 * - Wraps the panel in a react-router `<Link>`.
 * - On hover: bronze underline appears beneath the value
 *   (`group-hover:border-b group-hover:border-bronze-500`).
 * - Static tiles (Total / Shown / Avg Fit) do NOT get the bronze underline.
 *
 * ARIA per UI-SPEC § 9.3 — clickable tile gets aria-label
 * `"{label}: {value}, opens property detail"`; static gets the bare
 * `"{label}: {value}"` label.
 */
export function StatTile({
  label,
  value,
  clickable = false,
  href,
  subtitle,
  className,
}: StatTileProps) {
  const valueText = typeof value === 'number' ? value.toString() : value;
  const ariaLabel = `${label}: ${valueText}${
    clickable ? ', opens property detail' : ''
  }`;

  const inner = (
    <GlassPanel
      variant="strong"
      aria-label={ariaLabel}
      className={cn(
        'h-24 p-5 grid grid-rows-[auto_auto] gap-2 items-start',
        clickable && 'group hover:bg-ocean-950/80 transition-colors duration-150',
        !clickable && className,
      )}
    >
      <span className="text-sm font-semibold text-foam-50/70">{label}</span>
      <span
        className={cn(
          'text-xl font-semibold text-foam-50 tabular-nums truncate',
          clickable &&
            'group-hover:border-b group-hover:border-bronze-500 inline-block self-start',
        )}
      >
        {valueText}
      </span>
      {subtitle && (
        <span className="text-xs text-foam-50/50 row-start-3">{subtitle}</span>
      )}
    </GlassPanel>
  );

  if (clickable && href) {
    return (
      <Link
        to={href}
        className={cn(
          'rounded-2xl focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
          className,
        )}
      >
        {inner}
      </Link>
    );
  }

  return inner;
}
